import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client.config';
import { queryKeys } from '../react-query.config';
import { toast } from 'sonner';
import {
  type GoogleAdsPlacement,
  type GoogleAdsPlacementKey,
} from '../tenants/googleAdsPlacements';

interface GetPickemGoogleAdResponse {
  placement: GoogleAdsPlacement | null;
}

const DEFAULT_PLACEMENT_KEY: GoogleAdsPlacementKey = 'PICKEM_CONTEST_CARD';

export const getPickemGoogleAd = async (
  pickemId: string,
  placementKey: GoogleAdsPlacementKey = DEFAULT_PLACEMENT_KEY
): Promise<GetPickemGoogleAdResponse> => {
  return apiClient.get('/api/admin/pickems/{id}/google-ad' as any, {
    pathParams: { id: pickemId },
    params: { placementKey } as any,
  }) as any;
};

export function useGetPickemGoogleAdQuery(
  pickemId: string | undefined,
  placementKey: GoogleAdsPlacementKey = DEFAULT_PLACEMENT_KEY
) {
  return useQuery({
    queryKey: [...queryKeys.pickems.detail(pickemId || ''), 'google-ad', placementKey] as const,
    queryFn: () => getPickemGoogleAd(pickemId!, placementKey),
    enabled: !!pickemId,
    select: data => data.placement,
  });
}

export const putPickemGoogleAd = async (
  pickemId: string,
  placement: GoogleAdsPlacement
): Promise<GetPickemGoogleAdResponse> => {
  // The placement key is carried in the body — the backend upserts the row for
  // that (tenant, pickem, placementKey), so multiple per-pickem placements
  // (contest card + sticky footer) coexist.
  return apiClient.put('/api/admin/pickems/{id}/google-ad' as any, placement, {
    pathParams: { id: pickemId },
  }) as any;
};

export function usePutPickemGoogleAdMutation(
  pickemId: string | undefined,
  placementKey: GoogleAdsPlacementKey = DEFAULT_PLACEMENT_KEY
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (placement: GoogleAdsPlacement) => putPickemGoogleAd(pickemId!, placement),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pickems.all });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.pickems.detail(pickemId || ''), 'google-ad', placementKey] as const,
      });
      toast.success('Google Ad placement saved');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save Google Ad placement';
      toast.error(message);
    },
  });
}
